/**
 * Resolver geografici centralizzati per nomi completi
 * Usato per SEO: titoli, H1, breadcrumb, metadata, JSON-LD
 * NON usare sigle o toTitle() naive
 */

import PROVINCE from "@/data/province.json";
import GAZETTEER from "@/data/gazetteer_comuni.json";

export type ProvinciaInfo = {
  slug: string;
  name: string;
  sigla: string;
  regioneSlug: string;
  regioneName: string;
};

export type ComuneInfo = {
  nome: string;
  provincia: string;
  regione: string;
  slug: string;
};

/**
 * Risolve una provincia da slug → dati completi
 * @param slug - Slug della provincia (es. "genova", "la-spezia", "laquila")
 * @returns Info completa o fallback
 */
export function resolveProvincia(slug: string): ProvinciaInfo {
  const p = PROVINCE.find((x) => x.slug === slug);
  if (p) return p as ProvinciaInfo;

  // Fallback
  return {
    slug,
    name: slug,
    sigla: "",
    regioneSlug: "",
    regioneName: "",
  };
}

/**
 * Risolve una provincia da sigla → dati completi
 * @param sigla - Sigla della provincia (es. "GE", "SP", "AQ")
 * @returns Info completa o fallback
 */
export function resolveProvinciaBySigla(sigla: string): ProvinciaInfo {
  const p = PROVINCE.find((x) => x.sigla.toLowerCase() === sigla.toLowerCase());
  if (p) return p as ProvinciaInfo;

  // Fallback
  return {
    slug: sigla.toLowerCase(),
    name: sigla,
    sigla: sigla.toUpperCase(),
    regioneSlug: "",
    regioneName: "",
  };
}

/**
 * Ottieni il nome completo di una provincia (da slug o da sigla)
 * @param slugOrSigla - Slug della provincia (es. "genova", "la-spezia") o sigla (es. "ge", "sp", "GE", "SP")
 * @returns Nome completo (es. "Genova", "La Spezia", "L'Aquila")
 */
export function displayProvincia(slugOrSigla: string): string {
  // Prova prima con lo slug
  const bySlug = PROVINCE.find((x) => x.slug === slugOrSigla.toLowerCase());
  if (bySlug) return (bySlug as ProvinciaInfo).name;

  // Prova con la sigla (uppercase o lowercase)
  const bySigla = PROVINCE.find((x) => x.sigla.toLowerCase() === slugOrSigla.toLowerCase());
  if (bySigla) return (bySigla as ProvinciaInfo).name;

  // Fallback
  return capitalizeSlug(slugOrSigla);
}

/**
 * Ottieni la sigla di una provincia (da slug o da sigla)
 * @param slugOrSigla - Slug della provincia o sigla
 * @returns Sigla (es. "GE", "SP", "AQ")
 */
export function siglaProvincia(slugOrSigla: string): string {
  // Prova prima con lo slug
  const bySlug = PROVINCE.find((x) => x.slug === slugOrSigla.toLowerCase());
  if (bySlug) return (bySlug as ProvinciaInfo).sigla;

  // Prova con la sigla (uppercase o lowercase)
  const bySigla = PROVINCE.find((x) => x.sigla.toLowerCase() === slugOrSigla.toLowerCase());
  if (bySigla) return (bySigla as ProvinciaInfo).sigla;

  // Fallback
  return slugOrSigla.toUpperCase();
}

/**
 * Risolve una regione da slug → nome completo
 * @param slug - Slug della regione (es. "liguria", "abruzzo", "piemonte")
 * @returns Nome completo (es. "Liguria", "Abruzzo", "Piemonte")
 */
export function displayRegione(slug: string): string {
  const p = PROVINCE.find((x) => x.regioneSlug === slug);
  return p?.regioneName ?? capitalizeSlug(slug);
}

/**
 * Risolve un comune da slug → dati completi
 * @param slug - Slug del comune (es. "genova", "san-cipriano-picentino")
 * @returns Info completa o fallback
 */
export function resolveComune(slug: string): ComuneInfo | null {
  const c = GAZETTEER.find((x) => x.slug === slug);
  if (c) {
    return {
      nome: c.nome,
      provincia: c.provincia,
      regione: c.regione,
      slug: c.slug,
    };
  }
  return null;
}

/**
 * Ottieni il nome completo di un comune
 * @param slug - Slug del comune
 * @returns Nome completo (es. "Genova", "San Cipriano Picentino")
 */
export function displayComune(slug: string): string {
  const c = resolveComune(slug);
  return c?.nome ?? capitalizeSlug(slug);
}

/**
 * Ottieni la sigla della provincia di un comune
 * @param comuneSlug - Slug del comune
 * @returns Sigla provincia (es. "GE", "SA")
 */
export function siglaComuneProvincia(comuneSlug: string): string {
  const c = resolveComune(comuneSlug);
  return c?.provincia ?? "";
}

/**
 * Fallback: capitalizza uno slug (solo per emergenze)
 * @private
 */
function capitalizeSlug(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Ottieni tutte le province
 */
export function getAllProvince(): ProvinciaInfo[] {
  return PROVINCE as ProvinciaInfo[];
}

/**
 * Ottieni tutte le province di una regione
 */
export function getProvinceByRegione(regioneSlug: string): ProvinciaInfo[] {
  return PROVINCE.filter((p) => p.regioneSlug === regioneSlug) as ProvinciaInfo[];
}
